/* eslint-disable no-redeclare */
import { useCallback, useEffect, useState } from 'react';

import { ostroAdapter } from '../../services/ostroAdapter';
import { OSTRO_STORAGE_CHANGE_EVENT_NAME } from './constants';
import {
  OstroStorageEvent,
  OstroStorageNullableReturnValue,
  OstroStorageNullableSetStateValue,
  OstroStorageReturnValue,
} from './types';
import { deserialize, serialize } from './utils';

export function getStorageItem(key: string, defaultValue: any = null) {
  const value = ostroAdapter.ostro.storage.getItem(key);
  return value === null ? defaultValue : deserialize(value);
}

export function setStorageItem(key: string, value: any) {
  ostroAdapter.ostro.storage.setItem(key, serialize(value));
  window.dispatchEvent(new CustomEvent(OSTRO_STORAGE_CHANGE_EVENT_NAME, { detail: { key, value } }));
}

export function removeStorageItem(key: string) {
  ostroAdapter.ostro.storage.removeItem(key);
  window.dispatchEvent(new CustomEvent(OSTRO_STORAGE_CHANGE_EVENT_NAME, { detail: { key, value: null } }));
}

// This is to allow type inference based on whether a defaultValue is provided:
// const [myValue] = useStorage('myKey') <-- myValue is of type string | null
// const [myValue] = useStorage('myKey', 'myDefaultValue') <-- myValue is of type string
// const [myNumbers] = useStorage<number[]>('myKey') <-- myNumbers is of type number[] | null
// const [myNumbers] = useStorage<number[]>('myKey', [1, 2]) <-- myNumbers is of type number[]
function useStorage<T = string>(key: string): OstroStorageNullableReturnValue<T>;
function useStorage<T = string>(key: string, defaultValue: T): OstroStorageReturnValue<T>;
function useStorage<T = string>(key: string, defaultValue: T | null = null) {
  const { storage } = ostroAdapter.ostro;

  const [localValue, setLocalValue] = useState<T | null>(
    storage.getItem(key) === null ? defaultValue : deserialize(storage.getItem(key)!)
  );

  const onStorageChange = useCallback(
    (event: OstroStorageEvent<T>) => {
      if (event.detail.key === key) {
        setLocalValue(event.detail.value);
      }
    },
    [key]
  );

  useEffect(() => {
    const eventListener = (event: Event) => {
      onStorageChange(event as OstroStorageEvent<T>);
    };

    window.addEventListener(OSTRO_STORAGE_CHANGE_EVENT_NAME, eventListener);

    return () => {
      window.removeEventListener(OSTRO_STORAGE_CHANGE_EVENT_NAME, eventListener);
    };
  }, [onStorageChange]);

  const valueSetter = useCallback(
    (newValueOrFunction: OstroStorageNullableSetStateValue<T>) => {
      if (newValueOrFunction instanceof Function) {
        setStorageItem(key, newValueOrFunction(localValue));
      } else {
        setStorageItem(key, newValueOrFunction);
      }
    },
    [key, localValue]
  );

  const valueRemover = useCallback(() => {
    removeStorageItem(key);
  }, [key]);

  // This is needed in case the localValue is set to null so we return the defaultValue
  const value: T | null = localValue ?? defaultValue;

  return [value, valueSetter, valueRemover];
}

export default useStorage;
